import { ComponentProps, ReactNode, useMemo, useRef } from 'react';

import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useTitle from 'react-use/lib/useTitle';

import DynamicBreadcrumbs from 'src/common/components/Breadcrumbs';
import {
  DashboardPageHeader,
  DashboardPageToolbar,
  DashboardToolbarAside,
} from 'src/common/components/DashboardPageToolbar';
import DashboardPageScrollContext from 'src/common/components/dashboard/DashboardPageScrollContext';
import {
  dashboardPageFillBodySx,
  dashboardPageRootSx,
  dashboardPageScrollRegionSx,
  dashboardStickyToolbarSx,
} from 'src/common/components/dashboardPageLayout';
import PageBox from 'src/common/components/PageBox';

interface Props {
  children: ReactNode;
  title: string;
  toolbar?: ReactNode;
  /** Primary actions aligned with the page title (e.g. Add). */
  headerActions?: ReactNode;
  /** Show loading state in the scrollable content area. */
  isLoading?: boolean;
  /**
   * Give the body a tall min-height so tables / virtualized lists keep a usable
   * viewport after the title scrolls away. Default: true.
   */
  fill?: boolean;
  /**
   * @deprecated Page body always scrolls; kept for call-site compatibility.
   */
  scrollable?: boolean;
  pageBoxSx?: SxProps<Theme>;
  toolbarAsideVariant?: 'end' | 'grow' | 'full';
  pageBoxProps?: Omit<
    ComponentProps<typeof PageBox>,
    'children' | 'fill' | 'scrollable' | 'isLoading' | 'sx'
  >;
}

export default function DashboardListPageLayout({
  children,
  title,
  toolbar,
  headerActions,
  isLoading,
  fill = true,
  pageBoxSx,
  toolbarAsideVariant = 'grow',
  pageBoxProps,
}: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContextValue = useMemo(() => ({ scrollRef }), [scrollRef]);

  useTitle(`${title} - ${t('base.defaultTitle')}`);

  return (
    <DashboardPageScrollContext.Provider value={scrollContextValue}>
      <Box sx={dashboardPageRootSx}>
        <Box ref={scrollRef} sx={dashboardPageScrollRegionSx}>
          <DynamicBreadcrumbs />
          <DashboardPageHeader title={title} actions={headerActions} />
          {toolbar ? (
            <Box sx={dashboardStickyToolbarSx}>
              <DashboardPageToolbar edge="sticky">
                <DashboardToolbarAside variant={toolbarAsideVariant}>{toolbar}</DashboardToolbarAside>
              </DashboardPageToolbar>
            </Box>
          ) : null}
          <PageBox
            fill={false}
            isLoading={isLoading}
            sx={[fill ? dashboardPageFillBodySx : { width: '100%' }, pageBoxSx] as SxProps<Theme>}
            {...pageBoxProps}
          >
            {children}
          </PageBox>
        </Box>
      </Box>
    </DashboardPageScrollContext.Provider>
  );
}
